import type { BillingCycle } from '@weft/types';
import { BILLING_CYCLE_STATUSES } from '@weft/types';
import { dayjs } from '@weft/utils';
import type { ColumnsType } from 'antd/es/table';
import cn from 'classnames';
import { ARROW_ICON_DIRECTIONS } from '../../../components/icons/ArrowIcon/types/arrow-icon-direction';
import { TableSortArrowIcon } from '../../../components/icons/TableSortArrowIcon';
import { formatDate } from '../../../utils/date';
import { BillingCycleOverviewDotMenu } from './BillingCycleOverviewDotMenu';

export function getBillingCycleColumns(): ColumnsType<BillingCycle> {
  return [
    {
      key: 'status',
      title: <p className='font-sans font-semibold text-black'>Status</p>,
      dataIndex: 'status',
      className: '!pl-[0]',
      width:'8rem',
      render: (_, row) => {
        return (
          <div className={cn(
            'relative flex items-center font-medium uppercase',
            { 'text-active': row.status === BILLING_CYCLE_STATUSES.Active },
            { 'text-active': row.status === BILLING_CYCLE_STATUSES.Inactive },
            { 'text-style': row.status === BILLING_CYCLE_STATUSES.Default },
          )}
          >
            <span
              className={cn(
                'h-[29px] w-1 rounded-0md bg-gradient-to-b',
                { 'from-[#BDFF00] to-[#05FF00]': row.status === BILLING_CYCLE_STATUSES.Active },
                { 'from-[#00D1FF] to-[#00FFA3]': row.status === BILLING_CYCLE_STATUSES.Default },
                { 'from-[#DADADA] to-[#DBDBDB]': row.status === BILLING_CYCLE_STATUSES.Inactive },
              )
            }
            />
            <span className={cn(
              'rounded-2lg ml-2.5 px-[9px] py-[5px] font-sans',
              { 'bg-[#EBFFEF]': row.status === BILLING_CYCLE_STATUSES.Active },
              { 'bg-[#EBF3FF]': row.status === BILLING_CYCLE_STATUSES.Default },
              { 'bg-[#ECECEC] text-[#939393]': row.status === BILLING_CYCLE_STATUSES.Inactive },
            )}
            >
              {row.status}
            </span>
          </div>
        );
      },
    },
    {
      key: 'name',
      title: <p className='font-sans font-semibold text-black'>Cycle Name</p>,
      dataIndex: 'name',
      align:'left',
      render:(value)=> <span className='font-sans text-black'>{value}</span>,
    },
    {
      key: 'customers',
      title: <p className='font-sans font-semibold text-black'>Customer</p>,
      dataIndex: 'customers',
      align:'center',
      render:(value)=> <span className='font-sans text-black'>{value?.length}</span>,
    },
    {
      key: 'billingDay',
      title: <p className='font-sans font-semibold text-black'>Billing Day</p>,
      dataIndex: 'billingDay',
      align:'center',
      render:(value)=> <span className='font-sans text-black'>{`Monthly on the ${getOrdinalValue(value)}`}</span>,
    },
    {
      key: 'createTime',
      title: ({ sortColumns }) => {
        const sortedColumn = sortColumns?.find(({ column }) => column.key === 'createTime');

        return (
          <div className='flex items-center justify-center'>
            {sortedColumn?.order && (
              <TableSortArrowIcon direction={sortedColumn.order === 'ascend' ? ARROW_ICON_DIRECTIONS.UP : ARROW_ICON_DIRECTIONS.DOWN}/>
            )}
            <p className='ml-1 font-sans font-bold text-black'>Created</p>
          </div>
        );
      },
      dataIndex: 'createTime',
      align:'center',
      sorter: (billingCycle1, billingCycle2) => new Date(billingCycle1.createdAt).getTime() - new Date(billingCycle2.createdAt).getTime(),
      render: (value) => <span className='font-sans text-black'>{formatDate(value)}</span>,
    },
    {
      key: 'BillingCycleOptionsMenu',
      title: '',
      dataIndex: 'id',
      align: 'right',
      className: 'cursor-pointer text-center',
      render: (value, row) => <BillingCycleOverviewDotMenu billingCycle={row} />,
    },
  ];
}

function getOrdinalValue(day: number) {
  return dayjs().date(day).format('Do');
}